import pathLoopConfigs from './path-loop-config.js';

export default class PathLoop {
  constructor({
    pathAnimation,
    kvArcSvgSelectors = [],
    kvLoopObserveSelector = '.p-index__kv',
    pathColorIntervalMs = 3000,
    pathColorTransitionMs = 400,
    onColorStep = null,
    onPause = null
  }) {
    this.pathAnimation = pathAnimation;
    this.kvArcSvgSelectors = kvArcSvgSelectors;
    this.kvLoopObserveSelector = kvLoopObserveSelector;
    this.pathColorIntervalMs = pathColorIntervalMs;
    this.pathColorTransitionMs = pathColorTransitionMs;
    this.onColorStep = onColorStep;
    this.onPause = onPause;
    this.pathLoopConfigs = pathLoopConfigs;
    this.pathLoopStates = [];
    this.pathLoopsPaused = false;
    this.pathColorPendingResume = false;
    this.pathColorTimer = null;
    this.pathLoopObserver = null;
  }

  schedule(startDelay) {
    window.setTimeout(() => {
      this.pathLoopConfigs.forEach((config) => {
        const layers = config.layerSelectors
          .map((selector) => document.querySelector(selector))
          .filter(Boolean);

        if (layers.length !== config.layerSelectors.length) {
          return;
        }

        layers.slice(1).forEach((path) => this.preparePathLoopPath(path, config.loopReverse));

        const state = this.createPathLoopState(layers, config.loopReverse);
        if (state) {
          this.pathLoopStates.push(state);
        }
      });

      this.observeKvPathLoops();

      if (!this.pathLoopsPaused) {
        this.startPathLoops();
      }
    }, startDelay);
  }

  observeKvPathLoops() {
    if (this.pathLoopObserver) {
      return;
    }

    const target = document.querySelector(this.kvLoopObserveSelector);
    if (!target) {
      this.startPathLoops();
      return;
    }

    if (!('IntersectionObserver' in window)) {
      this.startPathLoops();
      return;
    }

    this.pathLoopObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.resumePathLoops();
          } else {
            this.pausePathLoops();
          }
        });
      },
      {
        root: null,
        threshold: 0
      }
    );

    this.pathLoopObserver.observe(target);
  }

  startPathLoops() {
    this.setPathLoopRenderingState(true);

    if (!this.pathLoopsPaused) {
      this.runPathColorStep();
    }

    this.scheduleNextPathColorStep();
  }

  clearPathColorTimer() {
    if (this.pathColorTimer === null) {
      return;
    }

    window.clearTimeout(this.pathColorTimer);
    this.pathColorTimer = null;
  }

  scheduleNextPathColorStep() {
    this.clearPathColorTimer();
    this.pathColorTimer = window.setTimeout(() => {
      if (this.pathLoopsPaused) {
        this.pathColorPendingResume = true;
        return;
      }

      this.runPathColorStep();
      this.scheduleNextPathColorStep();
    }, this.pathColorIntervalMs);
  }

  runPathColorStep() {
    this.advanceAllPathColors();

    if (this.onColorStep) {
      this.onColorStep();
    }
  }

  advanceAllPathColors() {
    if (!this.pathLoopStates.length) {
      return;
    }

    this.pathLoopStates.forEach((state) => {
      const fromIndex = state.currentIndex;
      const toIndex = (fromIndex + 1) % state.layers.length;

      this.pathAnimation.animatePathSwap(
        state.layers[fromIndex],
        state.metas[fromIndex],
        state.layers[toIndex],
        state.metas[toIndex],
        this.pathColorTransitionMs,
        () => {
          state.currentIndex = toIndex;
        }
      );
    });
  }

  setPathLoopRenderingState(isLooping) {
    this.kvArcSvgSelectors.forEach((selector) => {
      const svg = document.querySelector(selector);
      if (!svg) {
        return;
      }

      svg.classList.toggle('is-path-looping', isLooping);
    });
  }

  pausePathLoops() {
    if (this.pathLoopsPaused) {
      return;
    }

    this.pathLoopsPaused = true;
    this.clearPathColorTimer();

    if (this.onPause) {
      this.onPause();
    }

    this.pathLoopStates.forEach((state) => {
      state.layers.forEach((path) => {
        this.pathAnimation.cancelPathAnimations(path);
      });
      this.stabilizePathLoopState(state);
    });
    this.pathColorPendingResume = true;
  }

  resumePathLoops() {
    if (!this.pathLoopsPaused) {
      return;
    }

    this.pathLoopsPaused = false;

    if (!this.pathColorPendingResume) {
      return;
    }

    this.pathColorPendingResume = false;
    this.scheduleNextPathColorStep();
  }

  stabilizePathLoopState(state) {
    state.layers.forEach((path, index) => {
      const { dash, isReverse } = state.metas[index];
      this.pathAnimation.applyPathStrokeOpacity(path);

      if (index === state.currentIndex) {
        this.pathAnimation.applyPathDash(path, { dasharray: `${dash} ${dash}`, offset: 0 });
        this.pathAnimation.showPathLoopLayer(path);
        return;
      }

      this.pathAnimation.applyPathDash(path, {
        dasharray: `${dash} ${dash}`,
        offset: this.pathAnimation.getDrawStartOffset(dash, isReverse)
      });
      this.pathAnimation.hidePathLoopLayer(path);
    });
  }

  createPathLoopState(layers, loopReverse) {
    if (!layers.length) {
      return null;
    }

    return {
      layers,
      metas: layers.map((path) => this.pathAnimation.getPathDashMeta(path, loopReverse)),
      currentIndex: 0
    };
  }

  preparePathLoopPath(path, loopReverse) {
    const { dash, isReverse } = this.pathAnimation.getPathDashMeta(path, loopReverse);
    this.pathAnimation.applyPathDash(path, {
      dasharray: `${dash} ${dash}`,
      offset: this.pathAnimation.getDrawStartOffset(dash, isReverse)
    });
    this.pathAnimation.hidePathLoopLayer(path);
    this.pathAnimation.applyPathStrokeOpacity(path);
  }
}
